import React from 'react'
import _ from 'lodash'
import {Card} from "../../../components";
import {Calendar, Circle, Clock, LucideIcon, Users} from "lucide-react";
import type {EventCategoryModel} from "../types/LiveEvent";
import { formatCompactTime } from "../../../lib/date-time";

interface EventCardProps {
    eventDetail: EventViewModel;
    icon: LucideIcon;
    iconColor?: string;
    iconBgColor?: string;
    statusColor?: string;
    statusBgColor?: string;
    statusBorderColor?: string;
    borderColor?: string;
    isDetail?: boolean
}

interface EventViewModel {
    id: string;
    name: string;
    status: string;
    categories: EventCategoryModel[];
    startTime: Date | null;
    endTime: Date | null;
    totalAttendees: number;
    totalCivicUsers: number;
    totalNonCivicUsers: number;
    engagementRate: number;
}

const EventDetailCard: React.FC<EventCardProps> = ({
                                                       eventDetail,
                                                       icon: Icon,
                                                       iconBgColor = "#10B981",
                                                       statusColor = "#008236",
                                                       statusBgColor = "#DCFCE7",
                                                       statusBorderColor = "#B9F8CF",
                                                       borderColor = "#E6E6E6",
                                                       isDetail = false
                                                   }) => {
    const removeEmoji = (text: string) =>
        Array.from(text ?? "")
            .filter((char) => {
                const codePoint = char.codePointAt(0) ?? 0;
                return !(
                    (codePoint >= 0x1f300 && codePoint <= 0x1faff) ||
                    (codePoint >= 0x2600 && codePoint <= 0x27bf) ||
                    codePoint === 0xfe0f
                );
            })
            .join("")
            .replace(/\s{2,}/g, " ")
            .trim();

    return (
        <Card
            className={`mr-3 min-w-[300px] max-w-[300px] min-h-[210px] p-5 border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 flex flex-col`}
        >
            <div className="flex justify-start items-center mb-2">
                <div className="rounded-full h-[26px] w-[26px] mr-2 flex-shrink-0"
                     style={{backgroundColor: iconBgColor}}/>
                <div className="flex flex-col min-w-0 flex-1">
                    <span
                        className="text-lg font-bold truncate dark:text-gray-100"
                        title={eventDetail?.name}
                    >
                        {eventDetail?.name}
                    </span>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row justify-start items-start mb-2 text-[#4A5565] dark:text-gray-400">
                <p className={"md:flex justify-start items-center"}>
                    <Calendar className="mr-1 text-[#4A5565] dark:text-gray-400" size={12}></Calendar>
                    <span className="text-xs mr-3 pt-1">
                        {eventDetail?.startTime
                          ? eventDetail.startTime.toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "-"}
                    </span>
                </p>
                <p className={"md:flex justify-start items-center"}>
                    <Clock className="mr-1 text-[#4A5565] dark:text-gray-400" size={12}></Clock>
                    <span className="text-xs pt-1">
                      {eventDetail?.startTime || eventDetail?.endTime
                        ? `${eventDetail?.startTime ? formatCompactTime(eventDetail.startTime) : "-"} - ${eventDetail?.endTime ? formatCompactTime(eventDetail.endTime) : "-"}`
                        : "-"}
                    </span>
                </p>
            </div>

            <div className="flex flex-col justify-start items-start mb-2 flex-wrap gap-2">
                <p className="border px-2 pt-[1px] text-xs rounded-lg inline-flex items-center" style={{
                    borderColor: statusBorderColor,
                    backgroundColor: statusBgColor,
                    color: statusColor
                }}>
                    {eventDetail?.status}
                </p>

                <p className="mb-0 w-full flex justify-start items-center gap-2 flex-wrap">

                    {(() => {
                        const cats = eventDetail?.categories ?? [];
                        const shown = isDetail ? cats : _.take(cats, 5);

                        return (
                            <>
                                {shown.map((category) => (
                                    <span
                                        key={category.id}
                                        className="border-2 px-2 pt-[1px] text-xs rounded-lg inline-flex items-center dark:border-gray-600 dark:text-gray-300"
                                    >{removeEmoji(category.name)}</span>
                                ))}
                                {!isDetail && cats.length > 5 && (
                                    <span
                                        className="px-2 py-[2px] text-xs rounded-full border border-[#C2F1E7] bg-[#F2FCFA] color-[#004232] inline-flex items-center"> +{cats.length - 5}</span>
                                )}
                            </>
                        );
                    })()}
                </p>
            </div>


            <div className="flex flex-col justify-start h-full">
                <div className="grid gap-2 border rounded-lg dark:border-gray-600 p-2">
                    <div
                        className="flex justify-between items-center text-sm">
                        <div className="flex justify-start items-center text-[#004232] dark:text-gray-100">
                            <Users className="mr-1 " size={12}></Users>
                            <span className="pt-0.5 text-[#0A0A0A] dark:text-gray-100">Total Attendees</span>
                        </div>
                        <span
                            className="font-bold text-[#364153] dark:text-gray-100">{eventDetail?.totalAttendees.toLocaleString("en-US")}</span>
                    </div>
                    <div className="w-full text-[10px] text-[#4A5565] dark:text-gray-400 flex justify-end items-center gap-1">
                        <span>{eventDetail?.totalCivicUsers.toLocaleString("en-US")} registered users</span>
                        <Circle size="3" className="m-0 p-0 stroke-[#4A5565] fill-[#4A5565] dark:stroke-white dark:fill-white"/>
                        <span>{eventDetail?.totalNonCivicUsers.toLocaleString("en-US")} guests</span>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default EventDetailCard;
